/**
 * Add a column to the open table — the same card a new table's columns are
 * described with, in the same drawer, becoming one `ALTER TABLE … ADD COLUMN`.
 */
import { useEffect, useState } from 'react';
import { DsButton } from '../../components/ds';
import { quotedTarget } from './cells';
import ColumnCard, { ColumnSpec, columnDefinition, newColumnSpec } from './ColumnFields';
import SideDrawer, { DrawerPanel, touchSize } from './SideDrawer';
import { StatementDisclosure } from './StatementPreview';
import { useStatement } from './useStatement';

const AddColumnDialog = ({ appId, table, onClose, onDone }: {
  appId: string,
  /** Null while closed. */
  table: { schema: string, name: string } | null,
  onClose: () => void,
  onDone: () => void,
}) => {
  const [spec, setSpec] = useState<ColumnSpec>(() => newColumnSpec());
  const { run, isRunning, failure, reset } = useStatement(appId);
  const shown = table;

  useEffect(() => {
    if (table) {
      setSpec(newColumnSpec());
      reset();
    }
  }, [table, reset]);

  const definition = columnDefinition(spec);
  const sql = shown && definition
    ? `ALTER TABLE ${quotedTarget(shown.schema, shown.name)}\n  ADD COLUMN ${definition};`
    : '';
  const size = touchSize();

  const create = () => {
    if (sql) {
      run(sql, () => { onDone(); onClose(); });
    }
  };

  return (
    <SideDrawer open={!!table} onClose={onClose} width="md:w-[640px]">
      <DrawerPanel
        eyebrow={`schema ${shown?.schema || 'public'}`}
        title={`New column on ${shown?.name || ''}`}
        blurb="An existing row gets NULL unless you set a default."
        onClose={onClose}
        summary={`One ALTER TABLE statement on ${shown?.name || ''}.`}
        footer={
          <div className="flex items-center justify-end gap-2 flex-wrap ml-auto">
            <DsButton variant="ghost" size={size} onClick={onClose} isDisabled={isRunning}>Cancel</DsButton>
            <DsButton variant="primary" size={size} onClick={create} isDisabled={isRunning || !sql}>
              {isRunning ? 'Adding…' : 'Add column'}
            </DsButton>
          </div>
        }
      >
        <div className="flex flex-col gap-5">
          <ColumnCard
            label={`new column on ${shown?.name || ''}`}
            spec={spec}
            onChange={patch => setSpec(current => ({ ...current, ...patch }))}
            autoFocus
          />
          <StatementDisclosure sql={sql} emptyText="Name the column to see the statement." />
          {failure ? (
            <p className="m-0 font-courrier text-ds-115 leading-[1.5] text-state-err">{failure}</p>
          ) : null}
        </div>
      </DrawerPanel>
    </SideDrawer>
  );
};

export default AddColumnDialog;
